/**
 * Prefetch utilities for warming up components and data before navigation
 */

// Track what we've already prefetched to avoid duplicate work
const prefetchedModules = new Set<string>();
const prefetchedData = new Set<string>();

/**
 * Prefetch critical dashboard modules to warm up the module cache.
 * This makes the dashboard load faster when the user navigates to it.
 */
export const prefetchDashboardModules = async (): Promise<void> => {
    if (prefetchedModules.has('dashboard')) return;
    prefetchedModules.add('dashboard');

    // Import heavy modules in parallel to warm the cache
    // These are the main components used in the dashboard
    await Promise.all([
        import('../components/PDFCanvas'),
        import('../components/PDFToolbar'),
        import('../components/FileUpload'),
        import('../components/DetectionPanel'),
        import('../components/ResultsPanel'),
        import('../components/SideRail'),
    ]).catch(() => {
        // Silently fail - prefetching is best-effort
        prefetchedModules.delete('dashboard');
    });
};

/**
 * Prefetch critical assets like fonts and images
 */
export const prefetchAssets = (): void => {
    if (prefetchedData.has('assets')) return;
    prefetchedData.add('assets');

    const logoLink = document.createElement('link');
    logoLink.rel = 'prefetch';
    logoLink.href = '/logo.png';
    logoLink.as = 'image';
    document.head.appendChild(logoLink);

    if (document.fonts && typeof document.fonts.load === 'function') {
        document.fonts.load('400 16px Figtree').catch(() => {});
        document.fonts.load('500 16px Figtree').catch(() => {});
        document.fonts.load('600 16px Figtree').catch(() => {});
    }
};

/**
 * Prefetch subscription data for a user (call when auth is detected)
 * This warms the cache so the dashboard loads instantly
 */
export const prefetchSubscriptionData = async (userId: string, accessToken: string): Promise<void> => {
    const cacheKey = `subscription-${userId}`;
    if (prefetchedData.has(cacheKey)) return;
    prefetchedData.add(cacheKey);

    try {
        // Import supabase dynamically to avoid circular deps
        const { supabase } = await import('../lib/supabase');
        
        // Prefetch subscription data
        await supabase
            .from('subscriptions')
            .select('status, current_period_end, stripe_customer_id')
            .eq('user_id', userId)
            .single();
    } catch {
        // Silently fail - prefetching is best-effort
        prefetchedData.delete(cacheKey);
    }
};

/**
 * Main prefetch function - call from login page to warm everything up
 */
export const prefetchForDashboard = (): void => {
    // Use requestIdleCallback for non-critical prefetching
    const prefetch = () => {
        prefetchAssets();
        prefetchDashboardModules();
    };

    if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(prefetch, { timeout: 2000 });
    } else {
        // Fallback for Safari
        setTimeout(prefetch, 100);
    }
};
